/** @format */

import {
  CEOPS_CLIENT_RETRIES,
  CEOPS_CLIENT_TIMEOUT_MS,
  CEOPS_SERVER_RETRIES,
  CEOPS_SERVER_TIMEOUT_MS,
  type CeopsResponse,
  type CeopsScript,
  LinkRelTypes,
  ScriptTypes,
} from '../constants/ceops.constant';
import { Chain, Environment } from '@shared/data-access/interfaces';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
  input,
} from '@angular/core';
import { Location, isPlatformServer } from '@angular/common';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { filter, map, switchMap } from 'rxjs/operators';

import { CeopsService } from '../services/ceops.service';
import { Subscription } from 'rxjs';
import { UtilityService } from '@shared/data-access/services';

@Component({
  selector: 'shared-ceops',
  imports: [],
  templateUrl: './ceops.component.html',
  styleUrl: './ceops.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class CeopsComponent implements OnInit, OnDestroy {
  useCachedCeopsScripts = input<boolean>(false);
  /**
   * @description
   * Feature flag (kill switch) controlling social commerce–related CEOPS flows.
   */
  socialCommerceFlow = false;
  arrayAsyncScripts: HTMLScriptElement[] = [];
  ecommScriptsKillSwitch = false;
  subscription = new Subscription();

  private readonly ceopsService = inject(CeopsService);
  public readonly chain = inject(Chain);
  private readonly router = inject(Router);
  private readonly environment = inject(Environment);
  private readonly utilityService = inject(UtilityService);
  private readonly location = inject(Location);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    const chainAbbr = this.chain.chainIdentifierAbbr;

    this.socialCommerceFlow = this.utilityService.checkKillSwitch(
      'socialCommerceFlow',
      this.environment.killSwitch,
      chainAbbr,
    );
    this.ecommScriptsKillSwitch = this.utilityService.checkKillSwitch(
      'ecommScriptsEnabled',
      this.environment.killSwitch,
      chainAbbr,
    );

    // SRLP is using withEnabledBlockingInitialNavigation in the Router provider which prevents
    // earlier events like RoutesRecognized from firing during app bootstrap.
    const useNavigationEnd = this.location.path().startsWith('/search/');

    const isServer = isPlatformServer(this.platformId);
    const retries = isServer ? CEOPS_SERVER_RETRIES : CEOPS_CLIENT_RETRIES;
    const timeoutMs = isServer
      ? (this.environment.api.ceopsServerTimeoutMs ?? CEOPS_SERVER_TIMEOUT_MS)
      : CEOPS_CLIENT_TIMEOUT_MS;

    this.subscription.add(
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd | RoutesRecognized =>
            useNavigationEnd ? event instanceof NavigationEnd : event instanceof RoutesRecognized,
          ),
          map((route: NavigationEnd | RoutesRecognized) => route.url.split('?')[0]),
          switchMap((url) =>
            this.ceopsService.getScripts$(
              url,
              this.chain.chainMarketingIdentifierAbbr.toUpperCase(),
              retries,
              this.useCachedCeopsScripts(),
              this.ecommScriptsKillSwitch,
              timeoutMs,
            ),
          ),
        )
        .subscribe((response) => {
          const filteredResponse = this.filterCEOPSContent(response);
          this.handleCEOPSContent(filteredResponse);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public filterCEOPSContent(response: CeopsResponse | null) {
    if (!response || response.scripts.length <= 0) {
      return response;
    }

    const excludedPaths = new Set([
      '/one-offs/live-chat-link',
      '/botf/aos',
      '/botf/solepanel',
      '/botf/social-commerce',
      '/botf/device-fingerprint',
      '/aos/checkout',
      '/aos/reciept-printer',
    ]);

    if (this.socialCommerceFlow) {
      excludedPaths.add('/live-chat/index');
    }

    response.scripts = response.scripts.filter((script) => !excludedPaths.has(script.path));
    return response;
  }

  public handleCEOPSContent(response: CeopsResponse | null) {
    if (!response || response.scripts.length <= 0) {
      return;
    }

    const fragment = this.document.createDocumentFragment();
    const fragmentPreloads = this.document.createDocumentFragment();
    const isPlatformServerValue = isPlatformServer(this.platformId);
    const currentScripts = new Set<string>();

    if (!isPlatformServerValue) {
      const selector = `script[id*="ceopsScript___"]`;

      const scriptTags = this.document.querySelectorAll<HTMLScriptElement>(selector);
      for (const { src } of scriptTags) {
        if (src) {
          currentScripts.add(this.normalizeUrl(src));
        }
      }
    }

    response.scripts.forEach((script) => {
      if (!script.path) {
        return;
      }
      const id = `ceopsScript_${script.path.replace(/\//g, '__')}`;

      if (!isPlatformServerValue && !currentScripts.has(this.normalizeUrl(script.src))) {
        this.createScriptTag(fragment, script, id);
      } else if (isPlatformServerValue && script.loadSynchronously) {
        // Script tag
        this.createScriptTag(fragment, script, id);

        // Preload link tag
        const linkEl = this.document.createElement('link');
        const relValue = script.ecommScript ? LinkRelTypes.MODULE : LinkRelTypes.CLASSIC;
        linkEl.setAttribute('rel', relValue);
        if (script.src) {
          linkEl.setAttribute('href', script.src);
        }
        linkEl.setAttribute('as', 'script');
        linkEl.setAttribute('fetchpriority', 'high');
        fragmentPreloads.appendChild(linkEl);
      }
    });
    if (isPlatformServerValue) {
      this.document.head.appendChild(fragmentPreloads);
    }
    this.document.head.appendChild(fragment);
    setTimeout(() => {
      if (this.arrayAsyncScripts.length > 0) {
        const fragment = this.document.createDocumentFragment();
        this.arrayAsyncScripts.forEach((element) => {
          fragment.appendChild(element);
        });
        this.document.head.appendChild(fragment);
      }
    }, 0);
  }

  private createScriptTag(fragment: DocumentFragment, script: CeopsScript, id: string) {
    const newEl = this.document.createElement('script');
    const scriptType = script.ecommScript ? ScriptTypes.MODULE : ScriptTypes.CLASSIC;
    if (script.src) {
      newEl.src = script.src;
    }
    newEl.type = scriptType;
    newEl.id = id;

    if (!script.loadSynchronously) {
      newEl.async = true;
      this.arrayAsyncScripts.push(newEl);
    } else {
      fragment.appendChild(newEl);
    }
  }

  private normalizeUrl(urlString: string | undefined): string {
    if (!urlString) {
      return '';
    }
    const u = new URL(urlString);
    let path = u.pathname;
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    return u.origin + path;
  }
}
